import { useState } from 'react';

function CodeBlock({ code, language = 'json5' }: { code: string; language?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative group my-4">
      <div className="flex items-center justify-between bg-slate-800 rounded-t-lg px-4 py-2 border border-slate-700 border-b-0">
        <span className="text-xs text-slate-400 font-mono">{language}</span>
        <button onClick={handleCopy} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-400 transition-colors cursor-pointer whitespace-nowrap">
          <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
          {copied ? '已复制' : '复制'}
        </button>
      </div>
      <pre className="bg-slate-950 border border-slate-700 rounded-b-lg p-4 overflow-x-auto">
        <code className="text-sm text-slate-300 font-mono leading-relaxed">{code}</code>
      </pre>
    </div>
  );
}

function InfoBox({ type = 'info', children }: { type?: 'info' | 'warning' | 'tip'; children: React.ReactNode }) {
  const styles = {
    info: { bg: 'bg-cyan-500/5 border-cyan-500/20', icon: 'ri-information-line', iconColor: 'text-cyan-400' },
    warning: { bg: 'bg-amber-500/5 border-amber-500/20', icon: 'ri-alert-line', iconColor: 'text-amber-400' },
    tip: { bg: 'bg-emerald-500/5 border-emerald-500/20', icon: 'ri-lightbulb-line', iconColor: 'text-emerald-400' },
  };
  const s = styles[type];
  return (
    <div className={`${s.bg} border rounded-lg p-4 my-4`}>
      <div className="flex items-start gap-3">
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
          <i className={`${s.icon} ${s.iconColor} text-lg`}></i>
        </div>
        <div className="text-sm text-slate-300 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function SectionTitle({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="text-xl font-bold text-white mt-10 mb-4 flex items-center gap-2 scroll-mt-24">
      <span className="w-1 h-5 bg-cyan-400 rounded-full inline-block"></span>
      {children}
    </h2>
  );
}

function SubTitle({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h3 id={id} className="text-base font-semibold text-white mt-7 mb-3 scroll-mt-24">
      {children}
    </h3>
  );
}

export const ToolsOverviewContent = () => {
  return (
    <div>
      <h1 className="text-4xl font-bold text-white mb-5">工具概述</h1>
      <p className="text-base text-slate-300 mb-8 leading-relaxed">
        工具（Tools）是 Goose 智能体与外部世界交互的桥梁。通过工具系统，智能体可以执行各种操作，从简单的命令行执行到复杂的浏览器自动化。工具采用标准化接口设计，易于集成和扩展。
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 hover:border-cyan-500/30 transition-all">
          <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center mb-4">
            <i className="ri-tools-line text-2xl text-cyan-400"></i>
          </div>
          <div className="text-3xl font-bold text-white mb-2">20+</div>
          <p className="text-sm text-slate-400">内置工具</p>
        </div>

        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 hover:border-cyan-500/30 transition-all">
          <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center mb-4">
            <i className="ri-plug-line text-2xl text-purple-400"></i>
          </div>
          <div className="text-3xl font-bold text-white mb-2">插件化</div>
          <p className="text-sm text-slate-400">架构设计</p>
        </div>

        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 hover:border-cyan-500/30 transition-all">
          <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-4">
            <i className="ri-shield-check-line text-2xl text-emerald-400"></i>
          </div>
          <div className="text-3xl font-bold text-white mb-2">安全</div>
          <p className="text-sm text-slate-400">沙箱执行</p>
        </div>
      </div>

      <SectionTitle>核心工具</SectionTitle>
      
      <div className="space-y-5 mb-12">
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-terminal-box-line text-xl text-cyan-400"></i>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h4 className="text-lg font-semibold text-white">Shell 命令</h4>
                <span className="px-3 py-1 bg-cyan-500/10 text-cyan-400 text-sm rounded-full">
                  Core
                </span>
              </div>
              <p className="text-base text-slate-300 mb-3 leading-relaxed">
                执行 Shell 命令，支持管道、重定向和环境变量。可以运行系统命令、脚本和程序。
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-slate-700/50 text-slate-300 text-sm rounded-lg">bash</span>
                <span className="px-3 py-1 bg-slate-700/50 text-slate-300 text-sm rounded-lg">zsh</span>
                <span className="px-3 py-1 bg-slate-700/50 text-slate-300 text-sm rounded-lg">powershell</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-chrome-line text-xl text-purple-400"></i>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h4 className="text-lg font-semibold text-white">浏览器自动化</h4>
                <span className="px-3 py-1 bg-purple-500/10 text-purple-400 text-sm rounded-full">
                  Advanced
                </span>
              </div>
              <p className="text-base text-slate-300 mb-3 leading-relaxed">
                控制浏览器进行网页操作，包括导航、点击、填表、截图等。支持 Chrome 和 Firefox。
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-slate-700/50 text-slate-300 text-sm rounded-lg">Selenium</span>
                <span className="px-3 py-1 bg-slate-700/50 text-slate-300 text-sm rounded-lg">Playwright</span>
                <span className="px-3 py-1 bg-slate-700/50 text-slate-300 text-sm rounded-lg">Puppeteer</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-slash-commands-line text-xl text-emerald-400"></i>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h4 className="text-lg font-semibold text-white">斜杠命令</h4>
                <span className="px-3 py-1 bg-emerald-500/10 text-emerald-400 text-sm rounded-full">
                  Interactive
                </span>
              </div>
              <p className="text-base text-slate-300 mb-3 leading-relaxed">
                快捷命令系统，提供便捷的交互方式。支持自定义命令和参数补全。
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-slate-700/50 text-slate-300 text-sm rounded-lg">/help</span>
                <span className="px-3 py-1 bg-slate-700/50 text-slate-300 text-sm rounded-lg">/config</span>
                <span className="px-3 py-1 bg-slate-700/50 text-slate-300 text-sm rounded-lg">/session</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-group-line text-xl text-amber-400"></i>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h4 className="text-lg font-semibold text-white">子智能体</h4>
                <span className="px-3 py-1 bg-amber-500/10 text-amber-400 text-sm rounded-full">
                  Experimental
                </span>
              </div>
              <p className="text-base text-slate-300 mb-3 leading-relaxed">
                创建和管理子智能体，实现任务分解和并行处理。支持智能体间通信和协作。
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-slate-700/50 text-slate-300 text-sm rounded-lg">Delegation</span>
                <span className="px-3 py-1 bg-slate-700/50 text-slate-300 text-sm rounded-lg">Parallel</span>
                <span className="px-3 py-1 bg-slate-700/50 text-slate-300 text-sm rounded-lg">Communication</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SectionTitle>工具特性</SectionTitle>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <i className="ri-shield-check-line text-2xl text-cyan-400"></i>
            <h4 className="text-lg font-semibold text-white">安全沙箱</h4>
          </div>
          <p className="text-base text-slate-300 leading-relaxed">
            所有工具调用都在隔离的沙箱环境中执行，限制文件系统访问和网络权限，确保系统安全。
          </p>
        </div>

        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <i className="ri-time-line text-2xl text-purple-400"></i>
            <h4 className="text-lg font-semibold text-white">超时控制</h4>
          </div>
          <p className="text-base text-slate-300 leading-relaxed">
            支持设置工具执行超时时间，防止长时间运行的任务阻塞智能体响应。
          </p>
        </div>

        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <i className="ri-error-warning-line text-2xl text-emerald-400"></i>
            <h4 className="text-lg font-semibold text-white">错误处理</h4>
          </div>
          <p className="text-base text-slate-300 leading-relaxed">
            完善的错误处理机制，捕获和报告工具执行过程中的异常，提供详细的错误信息。
          </p>
        </div>

        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <i className="ri-file-list-3-line text-2xl text-amber-400"></i>
            <h4 className="text-lg font-semibold text-white">日志记录</h4>
          </div>
          <p className="text-base text-slate-300 leading-relaxed">
            记录所有工具调用的详细日志，包括输入参数、执行结果和性能指标，便于调试和审计。
          </p>
        </div>
      </div>

      <InfoBox type="info">
        工具系统采用异步执行模式，支持并发调用多个工具。智能体会自动管理工具的执行顺序和依赖关系。
      </InfoBox>

      <SectionTitle>快速开始</SectionTitle>
      
      <p className="text-base text-slate-300 mb-5 leading-relaxed">
        工具已经内置在 Goose 中，无需额外安装。您可以直接在对话中使用：
      </p>

      <CodeBlock language="bash">
{`# 使用 Shell 工具
goose chat "列出当前目录的文件"

# 使用浏览器工具
goose chat "打开 GitHub 并搜索 Goose 项目"

# 使用斜杠命令
/help tools
/config tools.browser.headless true`}
      </CodeBlock>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 hover:border-cyan-500/30 transition-all cursor-pointer">
          <div className="flex items-center gap-3 mb-3">
            <i className="ri-chrome-line text-2xl text-cyan-400"></i>
            <h4 className="text-lg font-semibold text-white">浏览器工具</h4>
          </div>
          <p className="text-base text-slate-400 mb-3">
            了解如何使用浏览器自动化工具进行网页操作
          </p>
          <span className="text-sm text-cyan-400 flex items-center gap-1">
            查看详细文档 <i className="ri-arrow-right-line"></i>
          </span>
        </div>

        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 hover:border-cyan-500/30 transition-all cursor-pointer">
          <div className="flex items-center gap-3 mb-3">
            <i className="ri-slash-commands-line text-2xl text-purple-400"></i>
            <h4 className="text-lg font-semibold text-white">斜杠命令</h4>
          </div>
          <p className="text-base text-slate-400 mb-3">
            学习如何使用和自定义斜杠命令
          </p>
          <span className="text-sm text-purple-400 flex items-center gap-1">
            查看命令列表 <i className="ri-arrow-right-line"></i>
          </span>
        </div>
      </div>
    </div>
  );
};

export default ToolsOverviewContent;
